import type { OfferingModel } from '@sosocio/creator-types/database';
import type {
	ArtworkSizeMetricUnits,
	PageModel,
	PageObjectModel,
	ProductDataModel,
} from '@sosocio/creator-types/project';
import type { ArtworkBoundingBoxObject } from '@sosocio/frontend-utils';
import { calculateArtworkSize } from '@sosocio/frontend-utils/artwork';

interface ArtworkPoint {
	x: number;
	y: number;
}

interface ArtworkSizesOptions {
	excludeEmptyPages?: boolean;
}

interface ClipBoundary {
	axis: 'x' | 'y';
	keepGreaterThanBoundary: boolean;
	value: number;
}

/**
 * Clips a rotated artwork polygon to the page rectangle.
 *
 * @param polygon Rotated artwork polygon in page coordinates.
 * @param pageModel {@link PageModel} that defines the printable clipping rectangle.
 * @returns Polygon points that remain visible inside the page rectangle.
 */
function clipArtworkPolygonToPage(
	polygon: ArtworkPoint[],
	pageModel: Pick<PageModel, 'height' | 'width'>,
): ArtworkPoint[] {
	const boundaries: ClipBoundary[] = [
		{
			axis: 'x',
			keepGreaterThanBoundary: true,
			value: 0,
		},
		{
			axis: 'x',
			keepGreaterThanBoundary: false,
			value: pageModel.width,
		},
		{
			axis: 'y',
			keepGreaterThanBoundary: true,
			value: 0,
		},
		{
			axis: 'y',
			keepGreaterThanBoundary: false,
			value: pageModel.height,
		},
	];

	return boundaries.reduce(
		(clippedPolygon, boundary) => clipPolygonToBoundary(
			clippedPolygon,
			boundary,
		),
		polygon,
	);
}

/**
 * Clips a polygon against one page boundary.
 *
 * @param polygon Polygon points in page coordinates.
 * @param boundary Page boundary to clip against.
 * @returns Polygon points inside the provided boundary.
 */
function clipPolygonToBoundary(
	polygon: ArtworkPoint[],
	boundary: ClipBoundary,
): ArtworkPoint[] {
	if (!polygon.length) {
		return [];
	}

	const clippedPolygon: ArtworkPoint[] = [];
	let previousPoint = polygon[polygon.length - 1];
	let previousPointInside = isPointInsideBoundary(
		previousPoint,
		boundary,
	);

	for (const currentPoint of polygon) {
		const currentPointInside = isPointInsideBoundary(
			currentPoint,
			boundary,
		);

		if (currentPointInside) {
			if (!previousPointInside) {
				clippedPolygon.push(getBoundaryIntersection(
					previousPoint,
					currentPoint,
					boundary,
				));
			}

			clippedPolygon.push(currentPoint);
		} else if (previousPointInside) {
			clippedPolygon.push(getBoundaryIntersection(
				previousPoint,
				currentPoint,
				boundary,
			));
		}

		previousPoint = currentPoint;
		previousPointInside = currentPointInside;
	}

	return clippedPolygon;
}

/**
 * Finds where a polygon segment crosses a page boundary.
 *
 * @param startPoint Segment start point.
 * @param endPoint Segment end point.
 * @param boundary Boundary crossed by the segment.
 * @returns Intersection point on the boundary.
 */
function getBoundaryIntersection(
	startPoint: ArtworkPoint,
	endPoint: ArtworkPoint,
	boundary: ClipBoundary,
): ArtworkPoint {
	const startValue = startPoint[boundary.axis];
	const endValue = endPoint[boundary.axis];
	const valueDelta = endValue - startValue;

	if (valueDelta === 0) {
		return {
			...endPoint,
		};
	}

	const ratio = (boundary.value - startValue) / valueDelta;

	return {
		x: startPoint.x + (endPoint.x - startPoint.x) * ratio,
		y: startPoint.y + (endPoint.y - startPoint.y) * ratio,
	};
}

/**
 * Converts clipped polygon points to axis-aligned artwork bounds.
 *
 * @param polygon Visible artwork polygon points.
 * @returns Axis-aligned visible artwork bounds, or null when the polygon has no area.
 */
function getClippedPolygonBounds(polygon: ArtworkPoint[]): ArtworkBoundingBoxObject | null {
	if (!polygon.length) {
		return null;
	}

	const xCoordinates = polygon.map((point) => point.x);
	const yCoordinates = polygon.map((point) => point.y);
	const minX = Math.min(...xCoordinates);
	const minY = Math.min(...yCoordinates);
	const maxX = Math.max(...xCoordinates);
	const maxY = Math.max(...yCoordinates);
	const width = maxX - minX;
	const height = maxY - minY;

	if (
		width <= 0
		|| height <= 0
	) {
		return null;
	}

	return {
		height,
		width,
		x_axis: minX,
		y_axis: minY,
	};
}

/**
 * Gets the artwork object's rotated corner polygon using Creator's center rotation model.
 *
 * @param objectModel Artwork object whose visual polygon should be calculated.
 * @returns Four rotated corner points in page coordinates.
 */
function getRotatedArtworkPolygon(objectModel: ArtworkBoundingBoxObject): ArtworkPoint[] {
	const rotation = (
		typeof objectModel.rotate === 'number'
		&& Number.isFinite(objectModel.rotate)
			? objectModel.rotate
			: 0
	);
	const radians = rotation * (Math.PI / 180);
	const cos = Math.cos(radians);
	const sin = Math.sin(radians);
	const centerX = objectModel.x_axis + objectModel.width / 2;
	const centerY = objectModel.y_axis + objectModel.height / 2;
	const points: ArtworkPoint[] = [
		{
			x: objectModel.x_axis,
			y: objectModel.y_axis,
		},
		{
			x: objectModel.x_axis + objectModel.width,
			y: objectModel.y_axis,
		},
		{
			x: objectModel.x_axis + objectModel.width,
			y: objectModel.y_axis + objectModel.height,
		},
		{
			x: objectModel.x_axis,
			y: objectModel.y_axis + objectModel.height,
		},
	];

	return points.map((point) => {
		const translatedX = point.x - centerX;
		const translatedY = point.y - centerY;

		return {
			x: centerX + translatedX * cos - translatedY * sin,
			y: centerY + translatedX * sin + translatedY * cos,
		};
	});
}

/**
 * Checks whether a point is inside a single clipping boundary.
 *
 * @param point Polygon point to check.
 * @param boundary Boundary that defines the accepted side.
 * @returns True when the point is inside the boundary.
 */
function isPointInsideBoundary(
	point: ArtworkPoint,
	boundary: ClipBoundary,
): boolean {
	const pointValue = point[boundary.axis];

	return boundary.keepGreaterThanBoundary
		? pointValue >= boundary.value
		: pointValue <= boundary.value;
}

/**
 * Clips an artwork object's rendered polygon to the printable page area.
 *
 * @param objectModel Artwork object whose rendered bounds should be clipped.
 * @param pageModel Page that defines the printable area.
 * @returns Axis-aligned visible artwork bounds, or null when nothing is printed.
 */
export function getVisiblePageArtworkObject(
	objectModel: ArtworkBoundingBoxObject,
	pageModel: Pick<PageModel, 'height' | 'width'>,
): ArtworkBoundingBoxObject | null {
	if (
		!Number.isFinite(pageModel.width)
		|| !Number.isFinite(pageModel.height)
		|| pageModel.width <= 0
		|| pageModel.height <= 0
	) {
		return null;
	}

	return getClippedPolygonBounds(clipArtworkPolygonToPage(
		getRotatedArtworkPolygon(objectModel),
		pageModel,
	));
}

/**
 * Rounds artwork size values to 2 decimal places and converts -0 to 0.
 *
 * @param value The artwork size value to round.
 * @returns The rounded artwork size value.
 */
function roundArtworkSizeValue(value: number): number {
	const roundedValue = Math.round((value + Number.EPSILON) * 100) / 100;

	return (
		Object.is(
			roundedValue,
			-0,
		)
			? 0
			: roundedValue
	);
}

/**
 * Gets all artwork objects on a page that have valid dimensions.
 *
 * @param pageModel {@link PageModel} to get objects from.
 * @param objects All objects in the product data, used to find the objects on the page.
 * @returns Array of artwork objects with valid dimensions on the page.
 */
function getPageArtworkObjects(
	pageModel: PageModel,
	objects: ProductDataModel['objects'],
): ArtworkBoundingBoxObject[] {
	if (
		pageModel.bgcolor
		&& pageModel.bgcolor.toLowerCase() !== 'transparent'
		&& Number.isFinite(pageModel.width)
		&& Number.isFinite(pageModel.height)
	) {
		return [
			{
				height: pageModel.height,
				width: pageModel.width,
				x_axis: 0,
				y_axis: 0,
			},
		];
	}

	if (!pageModel.objectList?.length) {
		return [];
	}

	return pageModel.objectList
		.map((objectId) => objects[objectId])
		.filter((objectModel): objectModel is PageObjectModel & ArtworkBoundingBoxObject => (
			!!objectModel
			&& Number.isFinite(objectModel.x_axis)
			&& Number.isFinite(objectModel.y_axis)
			&& Number.isFinite(objectModel.width)
			&& Number.isFinite(objectModel.height)
		))
		.map((objectModel) => getVisiblePageArtworkObject(
			objectModel,
			pageModel,
		))
		.filter((objectModel): objectModel is ArtworkBoundingBoxObject => !!objectModel);
}

/**
 * Calculates artwork dimensions for every page in the selected metric unit.
 *
 * @param productData Snapshot {@link ProductDataModel} to read from.
 * @param offeringModel {@link OfferingModel} that provides the config DPI.
 * @param metricUnits {@link ArtworkSizeMetricUnits} to store with the dimensions.
 * @param options Additional options for artwork size calculation.
 * @returns Artwork sizes keyed by page id.
 */
export function getArtworkSizesForProductData(
	productData: ProductDataModel,
	offeringModel: OfferingModel,
	metricUnits: ArtworkSizeMetricUnits,
	options: ArtworkSizesOptions = {},
): ProductDataModel['artworkSizes'] {
	const artworkSizes: NonNullable<ProductDataModel['artworkSizes']> = {};
	const pageModels = Object.values(productData.pages);

	if (productData.spine) {
		pageModels.push(productData.spine);
	}

	for (const pageModel of pageModels) {
		const pageArtworkObjects = getPageArtworkObjects(
			pageModel,
			productData.objects,
		);

		if (
			options.excludeEmptyPages
			&& !pageArtworkObjects.length
		) {
			continue;
		}

		const artworkSize = calculateArtworkSize(
			pageArtworkObjects,
			offeringModel.configdpi,
		);
		const size = artworkSize[metricUnits];

		artworkSizes[pageModel.id] = {
			height: roundArtworkSizeValue(size.height),
			metricUnits,
			width: roundArtworkSizeValue(size.width),
			x: roundArtworkSizeValue(size.x),
			y: roundArtworkSizeValue(size.y),
		};
	}

	return artworkSizes;
}

/**
 * Adds artwork dimensions to snapshot product data.
 *
 * @param productData Snapshot {@link ProductDataModel} to mutate.
 * @param offeringModel {@link OfferingModel} that provides the config DPI.
 * @param metricUnits {@link ArtworkSizeMetricUnits} to store with the dimensions.
 * @param options Additional options for artwork size calculation.
 */
export function addArtworkSizesToProductData(
	productData: ProductDataModel,
	offeringModel: OfferingModel,
	metricUnits: ArtworkSizeMetricUnits,
	options: ArtworkSizesOptions = {},
): void {
	productData.artworkSizes = getArtworkSizesForProductData(
		productData,
		offeringModel,
		metricUnits,
		options,
	);
}
